import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Icon from "../../components/Icon";
import LoadingBlock from "../../components/LoadingBlock";
import EmptyState from "../../components/EmptyState";
import Drawer from "../../components/Drawer";
import Pagination from "../../components/Pagination";
import ExpiryField from "../../components/ExpiryField";
import { useApi } from "../../hooks/useApi";
import { listFirms, createFirm } from "../../api/superAdminService";
import { paginatedRows, paginatedMeta } from "../../utils/pagination";
import { formatDate, isoPlusDays, isoPlusYears } from "../../utils/formatters";
import { showApiError, showToast } from "../../utils/toast";
import { showCredentials } from "../../utils/credentials";

const SUB_BADGE = {
  active: "badge-success",
  grace: "badge-warning",
  suspended: "badge-danger",
};

export default function FirmsListPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 250);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => { setPage(1); }, [debouncedSearch, statusFilter, perPage]);

  const firms = useApi(
    () => {
      const params = { page, per_page: perPage };
      if (debouncedSearch) params.search = debouncedSearch;
      if (statusFilter !== "all") params.status = statusFilter;
      return listFirms(params);
    },
    [page, perPage, debouncedSearch, statusFilter],
  );
  const rows = paginatedRows(firms.data);
  const meta = paginatedMeta(firms.data);

  return (
    <>
      <div className="page-head">
        <div>
          <h1 className="page-title">Firms</h1>
          <div className="page-sub">{meta?.total ?? rows.length} firm{(meta?.total ?? rows.length) === 1 ? "" : "s"} on the platform</div>
        </div>
        <div className="page-actions">
          <button className="btn btn-primary" onClick={() => setDrawerOpen(true)}>
            <Icon name="plus" size={14} /> Onboard firm
          </button>
        </div>
      </div>

      <div className="filter-bar">
        {[
          { id: "all", label: "All" },
          { id: "active", label: "Active" },
          { id: "suspended", label: "Suspended" },
        ].map((f) => (
          <button key={f.id} className={`chip ${statusFilter === f.id ? "active" : ""}`} onClick={() => setStatusFilter(f.id)}>
            {f.label}
          </button>
        ))}
        <div style={{ position: "relative", flex: 1, maxWidth: 360, marginLeft: "auto" }}>
          <Icon name="search" size={13} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "var(--ink-400)", pointerEvents: "none" }} />
          <input
            className="input"
            style={{ paddingLeft: 30 }}
            placeholder="Search by name or contact…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      <div className="card">
        <div className="card-body no-pad">
          {firms.loading ? (
            <div style={{ padding: 16 }}><LoadingBlock rows={5} /></div>
          ) : rows.length === 0 ? (
            <EmptyState icon="building" title="No firms match" hint={debouncedSearch ? "Try a different search." : "Onboard your first firm."} />
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>Firm</th>
                  <th>Status</th>
                  <th>Subscription</th>
                  <th className="t-num">Seats (used)</th>
                  <th>Period ends</th>
                  <th className="t-num">Users</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((f) => (
                  <tr key={f.id} style={{ cursor: "pointer" }} onClick={() => navigate(`/super/firms/${f.id}`)}>
                    <td>
                      <div className="t-primary">{f.name}</div>
                      {f.contact_name && <div className="t-muted text-xs">{f.contact_name} · {f.contact_phone}</div>}
                    </td>
                    <td>
                      {f.status === "active"
                        ? <span className="badge badge-success">Active</span>
                        : <span className="badge badge-danger">Suspended</span>}
                    </td>
                    <td>
                      {f.subscription
                        ? (
                          <span className="row gap-sm">
                            {f.subscription.is_trial && <span className="badge badge-purple">Trial</span>}
                            <span className={`badge ${SUB_BADGE[f.subscription.status] || "badge-neutral"}`}>{f.subscription.status}</span>
                          </span>
                        )
                        : <span className="badge badge-neutral">none</span>}
                    </td>
                    <td className="t-num">{f.subscription ? `${f.subscription.seats} (${f.seat_usage})` : "—"}</td>
                    <td>{f.subscription ? formatDate(f.subscription.period_end) : "—"}</td>
                    <td className="t-num">{f.users_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <Pagination meta={meta} page={page} onPageChange={setPage} perPage={perPage} onPerPageChange={setPerPage} disabled={firms.loading} />
        </div>
      </div>

      <OnboardFirmDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onCreated={(id) => { setDrawerOpen(false); firms.reload(); if (id) navigate(`/super/firms/${id}`); }}
      />
    </>
  );
}

function OnboardFirmDrawer({ open, onClose, onCreated }) {
  const empty = { name: "", contact_name: "", contact_phone: "", contact_email: "", seats: 5, admin_name: "", admin_phone: "", admin_username: "" };
  const [form, setForm] = useState(empty);
  const [isTrial, setIsTrial] = useState(false);
  const [periodEnd, setPeriodEnd] = useState(isoPlusYears(1));
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  useEffect(() => {
    if (!open) return;
    setForm(empty);
    setIsTrial(false);
    setPeriodEnd(isoPlusYears(1));
    setErrors({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Flipping the subscription type resets the expiry to that type's default.
  const pickType = (trial) => {
    setIsTrial(trial);
    setPeriodEnd(trial ? isoPlusDays(7) : isoPlusYears(1));
  };

  const presets = isTrial
    ? [
        { label: "7 days", iso: isoPlusDays(7) },
        { label: "14 days", iso: isoPlusDays(14) },
        { label: "30 days", iso: isoPlusDays(30) },
      ]
    : [
        { label: "+1 year", iso: isoPlusYears(1) },
        { label: "+2 years", iso: isoPlusYears(2) },
      ];

  const submit = async () => {
    setErrors({});
    setSaving(true);
    try {
      const res = await createFirm({
        ...form,
        contact_name: form.contact_name.trim() || null,
        contact_phone: form.contact_phone.trim() || null,
        contact_email: form.contact_email.trim() || null,
        seats: Number(form.seats),
        is_trial: isTrial,
        period_end: periodEnd,
      });
      if (!showCredentials(res)) showToast("success", "Firm created");
      onCreated?.(res?.data?.id);
    } catch (err) {
      if (err?.data?.errors) setErrors(err.data.errors);
      showApiError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="Onboard a firm"
      subtitle={isTrial
        ? "Creates the firm, its first admin (free seat) and a free trial."
        : "Creates the firm, its first admin (free seat) and a paid subscription."}
      footer={
        <>
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button
            className="btn btn-primary"
            onClick={submit}
            disabled={saving || !form.name.trim() || !form.admin_name.trim() || !form.admin_phone.trim() || !form.seats || !periodEnd}
          >
            {saving ? "Creating…" : "Create firm"}
          </button>
        </>
      }
    >
      <div className="field">
        <label className="field-label">Firm name</label>
        <input className="input" value={form.name} onChange={(e) => set({ name: e.target.value })} placeholder="e.g. Patel & Co" autoFocus />
        {errors.name && <div className="field-error">{errors.name[0]}</div>}
      </div>
      <div className="field">
        <label className="field-label">Subscription type</label>
        <div className="row gap-sm">
          <button type="button" className={`chip ${!isTrial ? "active" : ""}`} onClick={() => pickType(false)}>Paid</button>
          <button type="button" className={`chip ${isTrial ? "active" : ""}`} onClick={() => pickType(true)}>Trial</button>
        </div>
      </div>
      <ExpiryField
        isTrial={isTrial}
        value={periodEnd}
        onChange={setPeriodEnd}
        presets={presets}
        error={errors.period_end?.[0]}
      />
      <div className="field">
        <label className="field-label">{isTrial ? "Trial seats" : "Paid seats (min 5 billed)"}</label>
        <input className="input" type="number" min={1} value={form.seats} onChange={(e) => set({ seats: e.target.value })} />
        <div className="field-hint">
          {isTrial
            ? "Free trial — ₹0. Seats cap how many users the firm can add during the trial."
            : "₹100 per seat per month, collected yearly. The first admin is free."}
        </div>
        {errors.seats && <div className="field-error">{errors.seats[0]}</div>}
      </div>
      <div className="field">
        <label className="field-label">First admin — name</label>
        <input className="input" value={form.admin_name} onChange={(e) => set({ admin_name: e.target.value })} />
        {errors.admin_name && <div className="field-error">{errors.admin_name[0]}</div>}
      </div>
      <div className="field">
        <label className="field-label">First admin — phone</label>
        <input className="input" value={form.admin_phone} onChange={(e) => set({ admin_phone: e.target.value })} placeholder="9876543210" />
        <div className="field-hint">A one-time temp password is generated and shown once.</div>
        {errors.admin_phone && <div className="field-error">{errors.admin_phone[0]}</div>}
      </div>
      <div className="field">
        <label className="field-label">First admin — username (optional)</label>
        <input
          className="input"
          value={form.admin_username}
          onChange={(e) => set({ admin_username: e.target.value })}
          placeholder="Leave blank to auto-generate from phone"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
        />
        <div className="field-hint">Login username. Lowercase letters, digits, and . _ - only.</div>
        {errors.admin_username && <div className="field-error">{errors.admin_username[0]}</div>}
      </div>
      <div className="field">
        <label className="field-label">Contact person (optional)</label>
        <input className="input" value={form.contact_name} onChange={(e) => set({ contact_name: e.target.value })} />
      </div>
      <div className="field">
        <label className="field-label">Contact phone (optional)</label>
        <input className="input" value={form.contact_phone} onChange={(e) => set({ contact_phone: e.target.value })} />
      </div>
      <div className="field">
        <label className="field-label">Contact email (optional)</label>
        <input className="input" type="email" value={form.contact_email} onChange={(e) => set({ contact_email: e.target.value })} />
      </div>
    </Drawer>
  );
}
